import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, withCtx as _withCtx, createSlots as _createSlots, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import MeasurementBase, { Value } from './MeasurementBase.vue'
import { computed, inject, onMounted, onUnmounted, toRefs, watch } from 'vue'
import { FormatOptions, formatter } from '../formatter'
import { ColorScaleConfig, getColorForValue } from '@/utils/colorScale'
import useColoredEntities from '@/composables/useColoredEntities'
import { useGeneralStore } from '@/pinia'
import { useI18n } from 'vue-i18n'
import md5 from 'md5'
import { sharedDataKey } from '../../types'
import ColorLegend from '@/design-system/atoms/ColorLegend.vue'
import FeatureChildren from '@/components/FeatureChildren.vue'
import { Feature } from '@/pinia/feature'
import { storeToRefs } from 'pinia'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { EntityLevel, useEntityLevel } from '@/composables/useEntityLevel'

export type Coloring = {
  id: string
  meta?: any
  value: number
}

export type ColoredEntities = {
  colorings?: Coloring[]
  enabledEntityLevels?: EntityLevel[]
}

export type ColorLegend = {
  width?: number
  height?: number
  padding?: number
  hideMean?: boolean
  labelStart?: LocaleString
  labelEnd?: LocaleString
  format?: FormatOptions
  errorMessage?: string
}

type Props = {
  title: string
  name?: string
  info?: string
  value?: { isLoading?: boolean } & Value
  feature?: Feature
  colorScale?: ColorScaleConfig
  color?: string
  coloredEntities?: ColoredEntities
  colorLegend?: ColorLegend
  initialShowOnMap?: boolean
  absoluteColorScale?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MeasurementCard',
  props: {
    title: {},
    name: { default: undefined },
    info: { default: undefined },
    value: { default: undefined },
    feature: { default: undefined },
    colorScale: { default: undefined },
    color: { default: undefined },
    coloredEntities: { default: undefined },
    colorLegend: { default: () => ({
    height: 60,
    padding: 20,
    hideMean: false
  }) },
    initialShowOnMap: { type: Boolean, default: false },
    absoluteColorScale: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props

const piniaStore = useGeneralStore()

const { value, coloredEntities } = toRefs(props)

const { locale } = useI18n()

const coloredEntitiesStore = useColoredEntities()

const sharedData = inject(sharedDataKey)

const fullscreen = computed(() => sharedData?.fullscreen ?? false)

const { entityLevel } = storeToRefs(useEntityLevel())

const showOnMap = computed(() => coloredEntitiesStore.currentKey === key.value)

const hasColorings = computed(() => {
  return (
    (coloredEntities.value?.colorings && coloredEntities.value.colorings.length > 1) ||
    props.absoluteColorScale
  )
})

const onAllowedEntityLevel = computed(() => {
  return (
    (coloredEntities?.value?.enabledEntityLevels &&
      coloredEntities.value.enabledEntityLevels.includes(entityLevel.value)) ||
    (coloredEntities?.value && !coloredEntities.value.enabledEntityLevels)
  )
})

const showOnMapEnabled = computed(() => (hasColorings.value && onAllowedEntityLevel.value) ?? false)

const coloredEntitiesState = computed(() => {
  return coloredEntities.value?.colorings
})

const key = computed<string>(() => md5(props.name + props.title + props.feature?.name))

watch(
  coloredEntitiesState,
  () => {
    if (showOnMap.value) {
      coloredEntitiesStore.set({
        key: key.value,
        data: coloredEntitiesState.value,
        colorScale: props.colorScale,
        title: props.title,
        unit: '',
        formatter: (v) =>
          formatter(
            v,
            locale.value,
            value.value && 'format' in value.value ? value.value.format : undefined
          )
      })
    }
  },
  { deep: true }
)

onMounted(() => {
  if (
    (props.initialShowOnMap && !coloredEntitiesStore.currentKey) ||
    coloredEntitiesStore.currentKey === key.value
  ) {
    setShowOnMap(true)
  }
})

onUnmounted(() => {
  // coloredEntitiesStore.reset()
})

function setShowOnMap(state: boolean) {
  if (state) {
    coloredEntitiesStore.reset(true)
    coloredEntitiesStore.set({
      key: key.value,
      data: coloredEntitiesState.value,
      colorScale: props.colorScale,
      title: props.title,
      unit: '',
      formatter: (v) =>
        formatter(
          v,
          locale.value,
          value.value && 'format' in value.value ? value.value.format : undefined
        )
    })
    piniaStore.setSelectedAnalysisKPI(props.feature?.name)
  } else {
    coloredEntitiesStore.reset(true)
  }
}

function toggleShowOnMap() {
  setShowOnMap(!showOnMap.value)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(MeasurementBase, {
      title: _ctx.title,
      info: _ctx.info,
      value: _unref(value),
      color: 
      _ctx.color !== undefined
        ? _ctx.color
        : _unref(value) && 'number' in _unref(value) && _ctx.colorScale
        ? _unref(getColorForValue)(_ctx.colorScale, _unref(value).number).toHex()
        : _ctx.color
    ,
      showOnMapEnabled: showOnMapEnabled.value,
      showOnMap: showOnMap.value,
      isLoading: _unref(value)?.isLoading ?? true,
      fullscreen: fullscreen.value,
      onToggleShowOnMap: toggleShowOnMap
    }, _createSlots({ _: 2 }, [
      (_ctx.$slots.details)
        ? {
            name: "details",
            fn: _withCtx(() => [
              _renderSlot(_ctx.$slots, "details")
            ]),
            key: "0"
          }
        : undefined
    ]), 1032, ["title", "info", "value", "color", "showOnMapEnabled", "showOnMap", "isLoading", "fullscreen"]),
    (
      showOnMap.value &&
      showOnMapEnabled.value &&
      !fullscreen.value &&
      (!_ctx.feature?.children || _ctx.feature.children.length === 0) &&
      _unref(value) &&
      'number' in _unref(value) &&
      _unref(value).number !== undefined
    )
      ? (_openBlock(), _createBlock(ColorLegend, {
          key: 0,
          title: _ctx.title,
          width: _ctx.colorLegend?.width,
          height: _ctx.colorLegend?.height,
          padding: _ctx.colorLegend?.padding,
          format: _ctx.colorLegend?.format ?? _unref(value)?.format,
          labelStart: _ctx.colorLegend?.labelStart?.[_unref(locale)],
          labelEnd: _ctx.colorLegend?.labelEnd?.[_unref(locale)],
          colorScale: _ctx.colorScale,
          mean: _unref(value).number,
          hideMean: _ctx.colorLegend?.hideMean
        }, null, 8, ["title", "width", "height", "padding", "format", "labelStart", "labelEnd", "colorScale", "mean", "hideMean"]))
      : _createCommentVNode("", true),
    (
      showOnMap.value &&
      showOnMapEnabled.value &&
      !fullscreen.value &&
      _ctx.feature &&
      _unref(value) &&
      'number' in _unref(value) &&
      _unref(value).number !== undefined
    )
      ? (_openBlock(), _createBlock(FeatureChildren, {
          key: 1,
          value: _unref(value).number,
          feature: `home/*/*/*/data/*/${_ctx.feature.name}`
        }, null, 8, ["value", "feature"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})